import type { NoteType } from '@spotnote/shared/types'
import { h, icons } from '../dom'
import { t, type WidgetLocale } from '../i18n'
import type { WidgetState } from '../state'


export interface ToolbarHandlers {
  arm: (tool: NoteType) => void
  undo: () => void
  review: () => void
  setLocale: (locale: WidgetLocale) => void
  exit: () => void
}

const LANGUAGES: { value: WidgetLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
]

export function hintText(state: WidgetState): string {
  if (state.composer) return t().hintComposer
  if (!state.tool) return t().hintPick
  return state.tool === 'pin' ? t().hintPin : state.tool === 'rect' ? t().hintRect : t().hintDraw
}

export function noteCountLabel(count: number): string {
  return t().noteCount(count)
}

function toolButton(
  label: string,
  icon: string,
  tool: NoteType,
  state: WidgetState,
  arm: (t: NoteType) => void,
): HTMLElement {
  return h(
    'button',
    {
      class: 'tool',
      type: 'button',
      'aria-pressed': String(state.tool === tool),
      onclick: () => arm(tool),
    },
    h('span', { html: icon, style: 'display:flex;' }),
    label,
  )
}

export function renderToolbar(state: WidgetState, handlers: ToolbarHandlers): HTMLElement {
  const count = state.notes.length
  const whiteLabel = state.config?.brand.white_label ?? false

  const bar = h('div', { class: 'toolbar', role: 'toolbar', 'aria-label': t().toolbarAria })

  if (!whiteLabel) {
    bar.appendChild(h('div', { class: 'mark', html: icons.star }))
    bar.appendChild(h('div', { class: 'divider' }))
  }

  bar.appendChild(toolButton(t().toolNote, icons.pin, 'pin', state, handlers.arm))
  bar.appendChild(toolButton(t().toolHighlight, icons.rect, 'rect', state, handlers.arm))
  bar.appendChild(toolButton(t().toolSketch, icons.draw, 'draw', state, handlers.arm))
  bar.appendChild(h('div', { class: 'divider' }))

  const undo = h('button', {
    class: 'icon-btn',
    type: 'button',
    title: t().undo,
    'aria-label': t().undo,
    text: '↺',
    onclick: handlers.undo,
  }) as HTMLButtonElement
  undo.disabled = count === 0
  bar.appendChild(undo)

  // The note count rides on the Send button itself — the same counter the
  // launcher tile wears — so the number and the way out sit in one place.
  const send = h('button', {
    class: 'primary',
    type: 'button',
    text: t().send,
    'aria-label': count > 0 ? `${t().send} — ${noteCountLabel(count)}` : t().send,
    onclick: handlers.review,
  }) as HTMLButtonElement
  send.disabled = count === 0
  if (count > 0) {
    send.appendChild(h('span', { class: 'unsent-count', 'aria-hidden': 'true', text: String(count) }))
  }
  bar.appendChild(send)

  // The settings menu: a <details>, so open/close needs no store state — any
  // re-render (including the one a language switch triggers) closes it.
  bar.appendChild(
    h(
      'details',
      { class: 'lang' },
      h('summary', { 'aria-label': t().languageAria, title: t().languageAria, html: icons.globe }),
      h(
        'div',
        { class: 'lang-menu' },
        ...LANGUAGES.map((option) =>
          h('button', {
            class: 'lang-option',
            type: 'button',
            'aria-pressed': String(state.locale === option.value),
            text: option.label,
            onclick: () => handlers.setLocale(option.value),
          }),
        ),
      ),
    ),
  )

  bar.appendChild(
    h('button', {
      class: 'close',
      type: 'button',
      'aria-label': t().exitAria,
      text: '✕',
      onclick: handlers.exit,
    }),
  )

  return bar
}
